import {
    AccordionVariant,
    ExAccordion,
    ExAccordionItem,
    ExCheckbox,
    ExInput,
} from '@boomi/exosphere';
import { Copy } from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import translations from '../../../translations';
import type { RuntimeCreateResponse } from '../../../types/runtime';
import { generateRandomString } from '../../../utils/string';
import ButtonIcon from '../../buttons/ButtonIcon';
import ComponentWithTooltip from '../../generic/tooltip/ComponentWithTooltip';

interface Props {
    createdRuntime: RuntimeCreateResponse;
}

const LocalDockerCommand = ({ createdRuntime }: Props) => {
    const [name, setName] = useState(createdRuntime.developerName.replace(/\s/gi, ''));
    const [baseUrl, setBaseUrl] = useState('http://localhost:8080');
    const [containerPort, setContainerPort] = useState(80);
    const [databaseConnection, setDatabaseConnection] = useState(
        `Application Name=${createdRuntime.developerName};Database=flow;Host=localhost;Keepalive=300;Minimum Pool Size=5;Maximum Pool Size=50;Password=admin;Port=5432;SSL Mode=Prefer;Username=postgres;Include Error Detail=true;Command Timeout=60`,
    );
    const [storageHost, setStorageHost] = useState('');
    const [storagePort, setStoragePort] = useState(9001);
    const [storageAccessKey, setStorageAccessKey] = useState('');
    const [storageSecretKey, setStorageSecretKey] = useState('');
    const [syncInterval, setSyncInterval] = useState(60);
    const [syncBatchSize, setSyncBatchSize] = useState(100);
    const [otlpEndpoint, setOtlpEndpoint] = useState('');
    const [otlpHeaders, setOtlpHeaders] = useState('');
    const [otlpServiceName, setOtlpServiceName] = useState('FlowPlatformMcr');
    const [otlpEnvironment, setOtlpEnvironment] = useState('');
    const [user, setUser] = useState<'flowuser:flowgroup' | 'root'>('flowuser:flowgroup');

    const tokenEncryptionKey = useRef(generateRandomString(128));

    let dockerCommand = `docker run --name "${name}" -d \\
    -e FLOW_API_URL=${window.location.origin} \\
    -e INSTALLATION_TOKEN=${createdRuntime.installationToken} \\
    -e AUTHENTICATED_USER_TOKEN_ENCRYPTION_KEY=${tokenEncryptionKey.current} \\`;

    if (baseUrl) {
        dockerCommand += `\n    -e BASE_URL="${baseUrl}" \\`;
    }

    if (databaseConnection) {
        dockerCommand += `\n    -e DATABASE_CONNECTION_STRING="${databaseConnection}" \\`;
    }

    if (storageHost && storagePort && storageAccessKey && storageSecretKey) {
        dockerCommand += `\n    -e ManyWho_BlobStorage_Hostname="${storageHost}" \\
    -e ManyWho_BlobStorage_Port=${storagePort} \\
    -e ManyWho_BlobStorage_AccessKey=${storageAccessKey} \\
    -e ManyWho_BlobStorage_SecretKey=${storageSecretKey} \\`;
    }

    if (syncInterval) {
        dockerCommand += `\n    -e MCR_PING_INTERVAL_SECONDS=${syncInterval} \\`;
    }

    if (syncBatchSize) {
        dockerCommand += `\n    -e State_Sync_Batch_Size=${syncBatchSize} \\`;
    }

    if (otlpEndpoint) {
        dockerCommand += `\n    -e Otlp_Endpoint="${otlpEndpoint}" \\`;
    }

    if (otlpHeaders) {
        dockerCommand += `\n    -e Otlp_Headers="${otlpHeaders}" \\`;
    }

    if (otlpServiceName) {
        dockerCommand += `\n    -e Otlp_ServiceName="${otlpServiceName}" \\`;
    }

    if (otlpEnvironment) {
        dockerCommand += `\n    -e Otlp_Environment="${otlpEnvironment}" \\`;
    }

    dockerCommand += `\n    -u ${user} \\`;

    try {
        const hostUrl = new URL(baseUrl);

        let hostPort = hostUrl.port;

        if (!hostPort && hostUrl.protocol === 'http:') {
            hostPort = '80';
        } else if (!hostPort && hostUrl.protocol === 'https:') {
            hostPort = '443';
        }

        if (hostPort && containerPort) {
            dockerCommand += `\n    -p ${hostPort}:${containerPort} \\`;
        }
    } catch {}

    dockerCommand += '\n    boomi/flow:latest';

    return (
        <>
            <h5>{translations.LOCAL_RUNTIME_container_settings}</h5>
            <ExAccordion variant={AccordionVariant.FLAT}>
                <ExAccordionItem
                    label={translations.LOCAL_RUNTIME_container_settings_general}
                    data-testid="docker-container-settings-general"
                >
                    <div className="local-runtime-server-accordion-item">
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_name}
                            value={name}
                            onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
                            helpText={translations.LOCAL_RUNTIME_container_settings_name_help}
                        />
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_base_url}
                            value={baseUrl}
                            onInput={(e) => setBaseUrl((e.currentTarget as HTMLInputElement).value)}
                        />
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_port}
                            type="number"
                            value={containerPort.toString()}
                            onInput={(e) =>
                                setContainerPort(
                                    Number.parseInt(
                                        (e.currentTarget as HTMLInputElement).value,
                                        10,
                                    ),
                                )
                            }
                        />
                        <ExCheckbox
                            helpText={translations.LOCAL_RUNTIME_container_settings_user_help}
                            checked={user === 'root'}
                            onChange={() => {
                                const newUser = user === 'root' ? 'flowuser:flowgroup' : 'root';
                                setUser(newUser);
                            }}
                        >
                            {translations.LOCAL_RUNTIME_container_settings_user}
                        </ExCheckbox>
                    </div>
                </ExAccordionItem>
                <ExAccordionItem
                    label={translations.LOCAL_RUNTIME_container_settings_database}
                    data-testid="docker-container-settings-database"
                >
                    <div className="local-runtime-server-accordion-item">
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_database_conection}
                            value={databaseConnection}
                            onInput={(e) =>
                                setDatabaseConnection((e.currentTarget as HTMLInputElement).value)
                            }
                        />
                    </div>
                </ExAccordionItem>
                <ExAccordionItem
                    label={translations.LOCAL_RUNTIME_container_settings_storage}
                    data-testid="docker-container-settings-storage"
                >
                    <div className="local-runtime-server-accordion-item">
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_storage_host}
                            value={storageHost}
                            onInput={(e) =>
                                setStorageHost((e.currentTarget as HTMLInputElement).value)
                            }
                        />
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_storage_port}
                            value={storagePort.toString()}
                            type="number"
                            onInput={(e) =>
                                setStoragePort(
                                    Number.parseInt(
                                        (e.currentTarget as HTMLInputElement).value,
                                        10,
                                    ),
                                )
                            }
                        />
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_storage_accesskey}
                            value={storageAccessKey}
                            onInput={(e) =>
                                setStorageAccessKey((e.currentTarget as HTMLInputElement).value)
                            }
                        />
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_storage_secretkey}
                            value={storageSecretKey}
                            onInput={(e) =>
                                setStorageSecretKey((e.currentTarget as HTMLInputElement).value)
                            }
                        />
                    </div>
                </ExAccordionItem>
                <ExAccordionItem
                    label={translations.LOCAL_RUNTIME_container_settings_cloud_syncing}
                    data-testid="docker-container-settings-cloud-syncing"
                >
                    <div className="local-runtime-server-accordion-item">
                        <ExInput
                            label={
                                translations.LOCAL_RUNTIME_container_settings_cloud_syncing_interval
                            }
                            type="number"
                            value={syncInterval.toString()}
                            onInput={(e) =>
                                setSyncInterval(
                                    Number.parseInt(
                                        (e.currentTarget as HTMLInputElement).value,
                                        10,
                                    ),
                                )
                            }
                        />
                        <ExInput
                            label={
                                translations.LOCAL_RUNTIME_container_settings_cloud_syncing_batch_size
                            }
                            type="number"
                            value={syncBatchSize.toString()}
                            onInput={(e) =>
                                setSyncBatchSize(
                                    Number.parseInt(
                                        (e.currentTarget as HTMLInputElement).value,
                                        10,
                                    ),
                                )
                            }
                        />
                    </div>
                </ExAccordionItem>
                <ExAccordionItem
                    label={translations.LOCAL_RUNTIME_container_settings_otlp}
                    data-testid="docker-container-settings-otlp"
                >
                    <div className="local-runtime-server-accordion-item">
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_otlp_endpoint}
                            value={otlpEndpoint}
                            onInput={(e) =>
                                setOtlpEndpoint((e.currentTarget as HTMLInputElement).value)
                            }
                        />
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_otlp_headers}
                            value={otlpHeaders}
                            onInput={(e) =>
                                setOtlpHeaders((e.currentTarget as HTMLInputElement).value)
                            }
                        />
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_otlp_service_name}
                            value={otlpServiceName}
                            onInput={(e) =>
                                setOtlpServiceName((e.currentTarget as HTMLInputElement).value)
                            }
                        />
                        <ExInput
                            label={translations.LOCAL_RUNTIME_container_settings_otlp_environment}
                            value={otlpEnvironment}
                            onInput={(e) =>
                                setOtlpEnvironment((e.currentTarget as HTMLInputElement).value)
                            }
                        />
                    </div>
                </ExAccordionItem>
            </ExAccordion>
            <div>
                <span>{translations.LOCAL_RUNTIME_container_settings_docker_command}</span>
                <ComponentWithTooltip
                    trigger={'click'}
                    hideDelay={1000}
                    fadeTime={0.2}
                    tooltipContent="Copied"
                >
                    <ButtonIcon
                        className="copyable-icon"
                        onClick={() => navigator.clipboard.writeText(dockerCommand)}
                        title="Click to Copy"
                    >
                        <Copy />
                    </ButtonIcon>
                </ComponentWithTooltip>
            </div>
            <pre data-testid="local-runtime-docker-command">{dockerCommand}</pre>
        </>
    );
};

export default LocalDockerCommand;
